<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import LoginForm from '@/components/login/LoginForm.vue'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const externalError = ref<string>('')
const externalLoading = ref(false)

onMounted(async () => {
  const token = route.query.token as string | undefined
  if (!token) return
  // Single-use, but keep it out of history and later Referer headers anyway.
  await router.replace({ query: {} })

  // If the user is already logged in (e.g. an admin testing the partner link),
  // do not attempt to redeem the token or overwrite their session.
  // Redirect them to the dashboard and let the token expire naturally via TTL.
  if (auth.isAuthenticated) {
    await router.push('/')
    return
  }

  externalLoading.value = true
  try {
    const apiBase = import.meta.env.VITE_API_URL
    const res = await fetch(`${apiBase}/portal/enter/`, {
      method: 'POST',
      credentials: 'include', // receives the HttpOnly refresh cookie
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
    if (!res.ok) throw new Error('Token expired or already used.')
    const data = await res.json()
    await auth.loginWithAccess(data.access)
  } catch (err: unknown) {
    externalLoading.value = false
    externalError.value = (err as Error).message || 'External login failed.'
  }
})
</script>

<template>
  <div class="relative flex min-h-svh items-center justify-center bg-muted p-4">
    <div aria-hidden />
    <div class="absolute w-full h-full">
      <img
        class="h-full w-full absolute object-cover object-top"
        src="/background_pattern_1.svg"
        alt="UNSSC Background"
      />
    </div>
    <div class="h-full z-10">
      <!-- External token login: loading / error state -->
      <div v-if="externalLoading" class="flex flex-col items-center gap-4 text-white">
        <p class="text-lg font-semibold">Signing you in…</p>
      </div>
      <div v-else-if="externalError" class="flex flex-col items-center gap-4">
        <p class="text-red-400 font-semibold">{{ externalError }}</p>
        <LoginForm />
      </div>
      <!-- Normal login form (no external token in URL) -->
      <LoginForm v-else-if="!$route.query.token" />
    </div>
  </div>
</template>
